import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { config } from '@/config'

/**
 * Authorization Code + PKCE: the only browser flow that is safe for a public client, since no
 * client secret ever reaches the bundle.
 */
export const userManager = new UserManager({
  authority: config.keycloakUrl,
  client_id: config.keycloakClientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: `${window.location.origin}/`,
  response_type: 'code',
  scope: 'openid profile email',
  // Renew a few seconds before the access token expires, so a request in flight never carries an
  // expired one. Keycloak's default access token lifespan is 5 minutes.
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 60,
  silent_redirect_uri: `${window.location.origin}/auth/silent-renew`,
  // sessionStorage, not localStorage: the tokens die with the tab rather than lingering on a
  // shared machine. The Keycloak SSO cookie is what makes a new tab log in without a prompt.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
})

type AccessTokenClaims = {
  exp?: number
}

/**
 * Reads the payload of a JWT **without verifying it**. Only ever used to decide what the UI
 * shows; every authorization decision is made by the backend, which does verify the signature.
 */
export function decodeJwtPayload(token: string): AccessTokenClaims | null {
  const parts = token.split('.')
  if (parts.length !== 3) {
    return null
  }
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    return JSON.parse(atob(padded)) as AccessTokenClaims
  } catch {
    return null
  }
}

export function isTokenExpired(token: string | undefined): boolean {
  if (!token) {
    return true
  }
  const exp = decodeJwtPayload(token)?.exp
  if (typeof exp !== 'number') {
    return true
  }
  // 10s of slack for clock drift and for requests already on the wire.
  return exp * 1000 <= Date.now() + 10_000
}
